import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, Plus, Trash2, GripVertical, Eye, EyeOff, Settings,
  BarChart3, MousePointerClick, TrendingUp, Package, Search,
  FileText, Truck, UserPlus, RefreshCw, X, Send, Monitor
} from 'lucide-react'
import { emailPromosApi, PromoProduct, SearchProduct } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

export default function EmailPromosPage() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [showInactive, setShowInactive] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showTestModal, setShowTestModal] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewSubject, setPreviewSubject] = useState('')
  const [testEmail, setTestEmail] = useState('')
  const [previewLang, setPreviewLang] = useState('es')
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isSendingTest, setIsSendingTest] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Queries
  const productsQuery = useQuery({
    queryKey: ['email-promos-products', showInactive],
    queryFn: () => emailPromosApi.getProducts(showInactive)
  })

  const statsQuery = useQuery({
    queryKey: ['email-promos-stats'],
    queryFn: () => emailPromosApi.getStats()
  })

  const configQuery = useQuery({
    queryKey: ['email-promos-config'],
    queryFn: emailPromosApi.getConfig
  })

  // Mutations
  const addProductMutation = useMutation({
    mutationFn: (data: { prestashop_id?: number; reference?: string }) => emailPromosApi.addProduct(data),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Producto añadido a promociones')
        queryClient.invalidateQueries({ queryKey: ['email-promos-products'] })
        setShowAddModal(false)
        setSearchQuery('')
        setSearchResults([])
      } else {
        toast.error(result.error || 'Error al añadir producto')
      }
    },
    onError: () => toast.error('Error al añadir producto')
  })

  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => emailPromosApi.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-promos-products'] })
    },
    onError: () => toast.error('Error al actualizar producto')
  })

  const removeProductMutation = useMutation({
    mutationFn: (id: number) => emailPromosApi.removeProduct(id),
    onSuccess: () => {
      toast.success('Producto eliminado de promociones')
      queryClient.invalidateQueries({ queryKey: ['email-promos-products'] })
    },
    onError: () => toast.error('Error al eliminar producto')
  })

  const updateConfigMutation = useMutation({
    mutationFn: emailPromosApi.updateConfig,
    onSuccess: () => {
      toast.success('Configuración actualizada')
      queryClient.invalidateQueries({ queryKey: ['email-promos-config'] })
      setShowConfigModal(false)
    },
    onError: () => toast.error('Error al actualizar configuración')
  })

  // Search products
  const handleSearch = async () => {
    if (searchQuery.length < 2) return
    setIsSearching(true)
    try {
      const result = await emailPromosApi.searchProducts(searchQuery)
      setSearchResults(result.products || [])
    } catch {
      toast.error('Error en la búsqueda')
    } finally {
      setIsSearching(false)
    }
  }

  // Preview email
  const handlePreview = async () => {
    setIsLoadingPreview(true)
    try {
      const result = await emailPromosApi.getPreview(undefined, previewLang)
      setPreviewHtml(result.html)
      setPreviewSubject(result.subject)
      setShowPreviewModal(true)
    } catch {
      toast.error('Error generando preview')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  // Send test email
  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Introduce un email de destino')
      return
    }
    setIsSendingTest(true)
    try {
      const result = await emailPromosApi.sendTestEmail(testEmail, undefined, previewLang)
      if (result.success) {
        toast.success(result.message)
        setShowTestModal(false)
        setTestEmail('')
      }
    } catch {
      toast.error('Error enviando email de prueba')
    } finally {
      setIsSendingTest(false)
    }
  }

  const products = productsQuery.data?.products || []
  const stats = statsQuery.data?.summary
  const config = configQuery.data?.config

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl">
              <Mail className="w-6 h-6 text-white" />
            </div>
            Productos Promocionales
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Gestiona los productos que aparecen en los emails transaccionales
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handlePreview}
            disabled={isLoadingPreview}
            className="btn btn-secondary"
          >
            <Monitor className="w-4 h-4" />
            {isLoadingPreview ? 'Cargando...' : 'Vista Previa'}
          </button>
          <button
            onClick={() => setShowTestModal(true)}
            className="btn btn-secondary"
          >
            <Send className="w-4 h-4" />
            Enviar Prueba
          </button>
          <button
            onClick={() => setShowConfigModal(true)}
            className="btn btn-secondary"
          >
            <Settings className="w-4 h-4" />
            Configuración
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Añadir Producto
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Impresiones</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.impressions.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <MousePointerClick className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Clicks</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {stats.clicks.toLocaleString()}
                  <span className="text-sm font-normal text-slate-500 ml-1">({stats.ctr}% CTR)</span>
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Conversiones</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {stats.conversions}
                  <span className="text-sm font-normal text-slate-500 ml-1">({stats.conversion_rate}%)</span>
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <BarChart3 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Ingresos</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.revenue.toFixed(2)} €</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-slate-400" />
            <h2 className="font-semibold text-slate-900 dark:text-white">
              Productos Promocionados ({products.length})
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-slate-300 dark:border-slate-600"
              />
              Mostrar inactivos
            </label>
            <button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['email-promos-products'] })}
              className="btn btn-ghost btn-sm"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {productsQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">Cargando...</div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No hay productos promocionados</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary btn-sm mt-4"
            >
              <Plus className="w-4 h-4" />
              Añadir primer producto
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-10"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Producto</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <FileText className="w-4 h-4 mx-auto" title="Facturas" />
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <Truck className="w-4 h-4 mx-auto" title="Envíos" />
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <UserPlus className="w-4 h-4 mx-auto" title="Bienvenida" />
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Impresiones</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Clicks</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">CTR</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {products.map((product: PromoProduct) => (
                  <tr key={product.id} className={cn(
                    "hover:bg-slate-50 dark:hover:bg-slate-700/30",
                    !product.is_active && "opacity-50"
                  )}>
                    <td className="px-4 py-3">
                      <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.image_url && (
                          <img
                            src={product.image_url}
                            alt={product.name || product.reference}
                            className="w-10 h-10 object-contain rounded border border-slate-200 dark:border-slate-600"
                          />
                        )}
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {product.name || product.reference}
                          </p>
                          <p className="text-sm text-slate-500">
                            {product.reference} • {product.price?.toFixed(2)} €
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => updateProductMutation.mutate({
                          id: product.id,
                          data: { show_in_invoice: !product.show_in_invoice }
                        })}
                        className={cn(
                          "p-1.5 rounded-lg transition-colors",
                          product.show_in_invoice
                            ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                        )}
                      >
                        {product.show_in_invoice ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => updateProductMutation.mutate({
                          id: product.id,
                          data: { show_in_shipping: !product.show_in_shipping }
                        })}
                        className={cn(
                          "p-1.5 rounded-lg transition-colors",
                          product.show_in_shipping
                            ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                        )}
                      >
                        {product.show_in_shipping ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => updateProductMutation.mutate({
                          id: product.id,
                          data: { show_in_welcome: !product.show_in_welcome }
                        })}
                        className={cn(
                          "p-1.5 rounded-lg transition-colors",
                          product.show_in_welcome
                            ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                        )}
                      >
                        {product.show_in_welcome ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                      {product.stats?.impressions.toLocaleString() || 0}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                      {product.stats?.clicks.toLocaleString() || 0}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                      {product.stats?.ctr || 0}%
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => updateProductMutation.mutate({
                          id: product.id,
                          data: { is_active: !product.is_active }
                        })}
                        className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full transition-colors",
                          product.is_active
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                        )}
                      >
                        {product.is_active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          if (confirm('¿Eliminar este producto de las promociones?')) {
                            removeProductMutation.mutate(product.id)
                          }
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">Añadir Producto a Promociones</h3>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setSearchQuery('')
                  setSearchResults([])
                }}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Buscar por referencia o nombre..."
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isSearching || searchQuery.length < 2}
                  className="btn btn-primary"
                >
                  {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Buscar'}
                </button>
              </div>

              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {searchResults.map((product) => (
                  <div
                    key={product.id_product}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                      product.already_promoted
                        ? "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 opacity-60"
                        : "border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 cursor-pointer"
                    )}
                    onClick={() => {
                      if (!product.already_promoted) {
                        addProductMutation.mutate({ prestashop_id: product.id_product })
                      }
                    }}
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-12 h-12 object-contain rounded border border-slate-200 dark:border-slate-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">{product.name}</p>
                      <p className="text-sm text-slate-500">{product.reference} • {product.price.toFixed(2)} €</p>
                    </div>
                    {product.already_promoted ? (
                      <span className="text-xs text-slate-500 bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                        Ya añadido
                      </span>
                    ) : (
                      <Plus className="w-5 h-5 text-primary-600" />
                    )}
                  </div>
                ))}
                {searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                  <p className="text-center text-slate-500 py-4">No se encontraron productos</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && config && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">Configuración de Promociones</h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                updateConfigMutation.mutate({
                  products_per_email: parseInt(formData.get('products_per_email') as string),
                  attribution_window_days: parseInt(formData.get('attribution_window_days') as string),
                  promo_section_title_es: formData.get('promo_section_title_es') as string,
                  promo_section_title_en: formData.get('promo_section_title_en') as string,
                })
              }}
              className="p-4 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Productos por email
                </label>
                <input
                  type="number"
                  name="products_per_email"
                  defaultValue={config.products_per_email}
                  min={1}
                  max={6}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Ventana de atribución (días)
                </label>
                <input
                  type="number"
                  name="attribution_window_days"
                  defaultValue={config.attribution_window_days}
                  min={1}
                  max={30}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Título sección (Español)
                </label>
                <input
                  type="text"
                  name="promo_section_title_es"
                  defaultValue={config.promo_section_title_es}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Título sección (English)
                </label>
                <input
                  type="text"
                  name="promo_section_title_en"
                  defaultValue={config.promo_section_title_en}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowConfigModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateConfigMutation.isPending}
                  className="btn btn-primary"
                >
                  {updateConfigMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">Vista Previa del Email</h3>
                <p className="text-sm text-slate-500">Asunto: {previewSubject}</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={previewLang}
                  onChange={(e) => {
                    setPreviewLang(e.target.value)
                    emailPromosApi.getPreview(undefined, e.target.value).then(result => {
                      setPreviewHtml(result.html)
                      setPreviewSubject(result.subject)
                    })
                  }}
                  className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 p-4">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full min-h-[600px] bg-white rounded-lg shadow"
                title="Email Preview"
              />
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 shrink-0">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="btn btn-secondary"
              >
                Cerrar
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false)
                  setShowTestModal(true)
                }}
                className="btn btn-primary"
              >
                <Send className="w-4 h-4" />
                Enviar Prueba
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">Enviar Email de Prueba</h3>
              <button
                onClick={() => setShowTestModal(false)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Email de destino
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Idioma
                </label>
                <select
                  value={previewLang}
                  onChange={(e) => setPreviewLang(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <p className="text-sm text-slate-500">
                Se enviará un email de prueba con [TEST] en el asunto. No incluye PDF adjunto.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendTest}
                  disabled={isSendingTest || !testEmail}
                  className="btn btn-primary"
                >
                  {isSendingTest ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
